import { CircleHelp } from 'lucide-react';
import { useId, useRef, useState } from 'react';

const BUBBLE_WIDTH = 260;
const EDGE = 8;

/**
 * A "?" that explains on hover, focus or tap — for the sentence a screen needs
 * once and an agent then reads past every time it opens. The words are never
 * hover-only: the button carries them as its description, so a keyboard or a
 * screen reader gets the same explanation a mouse does.
 *
 * Positioned FIXED from the button's own box rather than absolutely inside its
 * parent: the contact panel is narrow and scrolls, the picker's list scrolls,
 * and either would clip a bubble drawn inside it. Clamped to the viewport, and
 * flipped above the button when there is no room below.
 */
export function InfoTip({ text, label = 'More info' }: { text: string; label?: string }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ left: number; top: number; above: boolean } | null>(null);

  const show = () => {
    const box = button.current?.getBoundingClientRect();
    if (!box) return;
    const left = Math.min(
      Math.max(EDGE, box.left + box.width / 2 - BUBBLE_WIDTH / 2),
      window.innerWidth - BUBBLE_WIDTH - EDGE,
    );
    const above = box.bottom > window.innerHeight - 140;
    setAt({ left, top: above ? box.top - 6 : box.bottom + 6, above });
  };
  const hide = () => setAt(null);

  return (
    <span className="infotip">
      <button
        ref={button}
        type="button"
        className="infotip-btn"
        aria-label={label}
        aria-describedby={id}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        // A tap has no hover: the press toggles it instead.
        onClick={() => (at ? hide() : show())}
        onKeyDown={(e) => {
          if (e.key === 'Escape') hide();
        }}
      >
        <CircleHelp size={13} aria-hidden />
      </button>
      <span id={id} className="infotip-sr">
        {text}
      </span>
      {at && (
        <span
          role="tooltip"
          className={`infotip-bubble${at.above ? ' above' : ''}`}
          style={{ left: at.left, top: at.top, maxWidth: BUBBLE_WIDTH }}
          aria-hidden
        >
          {text}
        </span>
      )}
    </span>
  );
}
